import { ReactNode, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { openInstallApp } from 'components/InstallApp/InstallApp';
import classNames from 'classnames';
import { UserContext } from 'context/userContext';
import { SlipContext } from 'context/slipContext';
import { formatBalanceDisplay } from 'helpers/balanceFormat';
import { ThemeMenuIcon } from 'components/ThemeMenuIcon/ThemeMenuIcon';
import type { MenuIconKey } from 'lib/menuIcons';
import { ModernSportSheet } from 'components/mobile/ModernSportNav/ModernSportSheet';
import { DEFAULT_TOTAL_STAKE, MAX_WIN_FOR_TICKET } from 'config/constants';
import { openSlipSheet, useSlipSheetOpen } from 'lib/slipSheet';
import { openLucky6Slip, useLucky6Slip } from 'lib/lucky6Bridge';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import { SportIDs } from 'types/enums';
import styles from './ModernChrome.module.css';

const CURRENCY = process.env.REACT_APP_CURRENCY ?? '';
const DEFAULT_STAKE = Number.parseFloat(
  process.env.REACT_APP_DEFAULT_STAKE ?? DEFAULT_TOTAL_STAKE
);

/** Sport and league of an offer page (/sports-betting/:sport/:id/:league/:id). */
function offerIds(url: string) {
  const p = url.split('/');
  if (p[1] !== 'sports-betting') return {};
  if (p[2] === 'sport') return { sportId: Number(p[4]) || undefined };
  if (p.length >= 6)
    return { sportId: Number(p[3]) || undefined, leagueId: Number(p[5]) };
  return {};
}

type NavItem = {
  icon: MenuIconKey;
  label: string;
  to: string;
  match: string[];
};

const NAV: NavItem[] = [
  { icon: 'home', label: 'home_label', to: '/', match: ['/'] },
  {
    icon: 'sports-betting',
    label: 'modern_nav_sports',
    to: '/sports-betting',
    match: ['/sports-betting', '/results', '/search'],
  },
  {
    icon: 'live-betting',
    label: 'modern_nav_live',
    to: '/live-betting',
    match: ['/live-betting'],
  },
  {
    icon: 'casino-games',
    label: 'modern_nav_casino',
    to: '/casino-games',
    match: ['/casino-games', '/live-casino', '/lucky-six', '/keno'],
  },
  {
    icon: 'my-bets',
    label: 'my_bets_label',
    to: '/my-bets',
    match: ['/my-bets'],
  },
];

function isActive(url: string, item: NavItem) {
  return item.match.some((m) =>
    m === '/' ? url === '/' : url === m || url.startsWith(m + '/')
  );
}

type HeaderProps = {
  title: ReactNode;
  hideBack: boolean;
  trailing?: ReactNode;
  onMenu: () => void;
};

export function ModernHeader({
  title,
  hideBack,
  trailing,
  onMenu,
}: HeaderProps) {
  const { loggedUser } = useContext(UserContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const url = useLocation().pathname;
  const greeting = url === '/' && loggedUser;

  return (
    <div className={styles.header}>
      {!hideBack && (
        <button
          type="button"
          className={styles.back}
          aria-label={t('back_label')}
          onClick={() => navigate(-1)}
        >
          <ThemeMenuIcon icon="chevron-left" alt="" variant="footer" />
        </button>
      )}
      <div className={styles.titleBox}>
        {greeting ? (
          <>
            <span className={styles.hello}>
              {t('modern_hello', {
                name: loggedUser.first_name || loggedUser.username,
              })}{' '}
              <span aria-hidden>👋</span>
            </span>
            <span className={styles.welcome}>{t('modern_welcome')}</span>
          </>
        ) : (
          <span className={styles.title}>{title}</span>
        )}
      </div>
      {trailing != null && <div className={styles.trailing}>{trailing}</div>}
      {loggedUser ? (
        <button type="button" className={styles.balancePill} onClick={onMenu}>
          <span className={styles.balance}>
            {formatBalanceDisplay(loggedUser.balance)}
            <small>{CURRENCY}</small>
          </span>
          <span className={styles.menuDot}>
            <ThemeMenuIcon
              icon="other"
              alt={t('menu_label')}
              variant="footer"
            />
          </span>
        </button>
      ) : (
        <button
          type="button"
          className={styles.loginPill}
          onClick={() => navigate('/login')}
        >
          {t('login_label')}
        </button>
      )}
    </div>
  );
}

/** American football, soccer and basketball on the sports menu button. */
const FAB_SPORTS = [12, SportIDs.SOCCER, SportIDs.BASKETBALL];

export function ModernFooter() {
  const url = useLocation().pathname;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getTotalBets, maxCoefficient, betNumber, payoutCoefficient } =
    useContext(SlipContext);
  const [sheetOpen, setSheetOpen] = useState(false);
  const slipCount: number = getTotalBets?.() ?? 0;
  const slipSheetOpen = useSlipSheetOpen();
  const onSlip = url === '/bet-slip' || slipSheetOpen;
  // The offer picker button lives on the sports pages; the slip is a bar
  // over the tab bar as soon as it has a pick.
  const showPicker = url.startsWith('/sports-betting');
  const showSlipBar = !onSlip && slipCount > 0;
  // The default stake on every bet; the best combinations win.
  const stake = DEFAULT_STAKE * Math.max(betNumber ?? 1, 1);
  const coef = Number(maxCoefficient ?? 0);
  const gain = Math.min(
    stake * Number(payoutCoefficient ?? 0),
    MAX_WIN_FOR_TICKET
  );
  const ids = offerIds(url);
  // On the Lucky Six page its ticket gets a bar of its own, stacked above
  // the sports one.
  const l6 = useLucky6Slip();
  const showLucky6Bar = url === '/lucky-six' && l6.count > 0;

  return (
    <>
      {sheetOpen && (
        <ModernSportSheet
          close={() => setSheetOpen(false)}
          sportId={ids.sportId}
          leagueId={ids.leagueId}
        />
      )}
      {showPicker && (
        <button
          type="button"
          className={classNames(styles.slipFab, showSlipBar && styles.fabUp)}
          onClick={() => setSheetOpen(true)}
          aria-label={t('sheet_sport')}
        >
          {/* A few balls together, like the sports menu of the reference app. */}
          <span className={styles.fabBalls} aria-hidden>
            {FAB_SPORTS.map((id) => (
              <SportIcon key={id} sportId={id} className={styles.fabBall} />
            ))}
          </span>
        </button>
      )}
      {showLucky6Bar && (
        <button
          type="button"
          className={classNames(
            styles.slipBar,
            styles.lucky6Bar,
            showSlipBar && styles.lucky6Stacked
          )}
          onClick={openLucky6Slip}
          aria-label={t('lucky_six_label')}
        >
          <span>
            <small className={styles.lucky6Tag}>LUCKY 6</small>
            <strong>
              {l6.count} {t('lucky_six_bets')}
            </strong>
          </span>
          <span>
            <small>{t('payment_label')}</small>
            <strong>
              {(l6.stake / 100).toFixed(2)} <em>{l6.currency || CURRENCY}</em>
            </strong>
          </span>
          <span>
            <small>{t('max_gain_label')}</small>
            <strong>
              {(l6.maxWin / 100).toFixed(2)} <em>{l6.currency || CURRENCY}</em>
            </strong>
          </span>
          <span className={styles.slipOpen}>
            <svg viewBox="0 0 24 24" aria-hidden>
              <path
                d="M6 15l6-6 6 6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className={styles.slipCount}>{l6.count}</span>
          </span>
        </button>
      )}
      {showSlipBar && (
        <button
          type="button"
          className={styles.slipBar}
          onClick={openSlipSheet}
          aria-label={t('slip_label')}
        >
          <span>
            <small>{t('max_coefficient_label')}</small>
            <strong>{coef.toFixed(2)}</strong>
          </span>
          <span>
            <small>{t('payment_label')}</small>
            <strong>
              {stake.toFixed(2)} <em>{CURRENCY}</em>
            </strong>
          </span>
          <span>
            <small>{t('max_gain_label')}</small>
            <strong>
              {gain.toFixed(2)} <em>{CURRENCY}</em>
            </strong>
          </span>
          <span className={styles.slipOpen}>
            <svg viewBox="0 0 24 24" aria-hidden>
              <path
                d="M6 15l6-6 6 6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className={styles.slipCount}>{slipCount}</span>
          </span>
        </button>
      )}
      <nav className={styles.footer}>
        {NAV.map((item) => (
          <button
            type="button"
            key={item.to}
            className={classNames(
              styles.navItem,
              isActive(url, item) && styles.navItemActive
            )}
            onClick={() => navigate(item.to)}
          >
            <ThemeMenuIcon icon={item.icon} alt="" variant="footer" />
            <span>{t(item.label)}</span>
          </button>
        ))}
      </nav>
    </>
  );
}

type DrawerItem = {
  icon: MenuIconKey;
  label: string;
  hint?: string;
  to?: string;
  onClick?: () => void;
  danger?: boolean;
};

export function ModernDrawer({
  close,
  dropdown = false,
}: {
  close: () => void;
  /** Desktop: a panel under the balance pill instead of a side drawer. */
  dropdown?: boolean;
}) {
  const { loggedUser, logout, refreshBalance } = useContext(UserContext);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [spinning, setSpinning] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  const go = (to: string) => {
    close();
    navigate(to);
  };

  // Always offered (owner, 2026-10-30); see InstallApp for what it opens.
  const installItem: DrawerItem[] = [
    {
      icon: 'mobile-app',
      label: 'install_app_label',
      hint: 'install_app_hint',
      onClick: () => {
        close();
        openInstallApp();
      },
    },
  ];

  const sections: { title: string; items: DrawerItem[] }[] = loggedUser
    ? [
        {
          title: 'modern_section_account',
          items: [
            {
              icon: 'my-bets',
              label: 'my_bets_label',
              hint: 'modern_hint_my_bets',
              to: '/my-bets',
            },
            {
              icon: 'account-statement',
              label: 'account_statement_label',
              hint: 'modern_hint_transactions',
              to: '/account-statement',
            },
            {
              icon: 'gaming-history',
              label: 'gaming_history_label',
              hint: 'modern_hint_gaming_history',
              to: '/gaming-history',
            },
            {
              icon: 'deposit',
              label: 'deposit_label',
              hint: 'modern_hint_deposit',
              to: '/deposit',
            },
            {
              icon: 'account-settings',
              label: 'account_settings_label',
              hint: 'modern_hint_account_settings',
              to: '/account-settings',
            },
            {
              icon: 'deposit',
              label: 'my_bonuses_label',
              hint: 'my_bonuses_hint',
              to: '/my-bonuses',
            },
          ],
        },
        {
          title: 'modern_section_preferences',
          items: [
            {
              icon: 'language',
              label: 'language_label',
              hint: i18n.language.toUpperCase(),
              to: '/language',
            },
            {
              icon: 'theme',
              label: 'theme_settings_label',
              hint: 'modern_hint_theme',
              to: '/theme-settings',
            },
          ],
        },
        {
          title: 'modern_section_info',
          items: [
            { icon: 'results', label: 'results_label', to: '/results' },
            // Desktop searches from the sports betting page.
            {
              icon: 'search',
              label: 'search_label',
              to: dropdown ? '/sports-betting' : '/search',
            },
            { icon: 'contact', label: 'contact_label', to: '/contact' },
            { icon: 'info', label: 'Info', to: '/info' },
            ...installItem,
          ],
        },
      ]
    : [
        {
          title: 'modern_section_info',
          items: [
            { icon: 'login', label: 'login_label', to: '/login' },
            { icon: 'language', label: 'language_label', to: '/language' },
            { icon: 'results', label: 'results_label', to: '/results' },
            { icon: 'contact', label: 'contact_label', to: '/contact' },
            ...installItem,
          ],
        },
      ];

  return (
    <div
      className={classNames(styles.drawerRoot, dropdown && styles.dropdownRoot)}
      role="dialog"
      aria-modal="true"
    >
      <div className={styles.scrim} onClick={close} />
      <aside className={styles.drawer}>
        <div className={styles.drawerTop}>
          <div className={styles.userRow}>
            <span className={styles.avatar}>
              <ThemeMenuIcon icon="person" alt="" variant="footer" />
            </span>
            <span className={styles.userName}>
              {loggedUser?.username ?? 'Tottalo'}
            </span>
            {loggedUser && (
              <button
                type="button"
                className={classNames(styles.iconBtn, spinning && styles.spin)}
                aria-label={t('modern_refresh_balance')}
                onClick={async () => {
                  setSpinning(true);
                  try {
                    await refreshBalance?.(true);
                  } finally {
                    setSpinning(false);
                  }
                }}
              >
                ⟳
              </button>
            )}
            <button
              type="button"
              className={styles.iconBtn}
              aria-label={t('close_label')}
              onClick={close}
            >
              ✕
            </button>
          </div>
          {loggedUser && (
            <div className={styles.balanceCard}>
              <ThemeMenuIcon icon="deposit" alt="" variant="footer" />
              <div>
                <strong>
                  {formatBalanceDisplay(loggedUser.balance)}{' '}
                  <small>{CURRENCY}</small>
                </strong>
                <span>{t('modern_balance_label')}</span>
              </div>
            </div>
          )}
        </div>
        <div className={styles.drawerBody}>
          {sections.map((section) => (
            <div key={section.title} className={styles.section}>
              <p className={styles.sectionTitle}>{t(section.title)}</p>
              {section.items.map((item) => (
                <button
                  type="button"
                  key={item.label}
                  className={styles.drawerItem}
                  onClick={() => (item.to ? go(item.to) : item.onClick?.())}
                >
                  <ThemeMenuIcon icon={item.icon} alt="" variant="footer" />
                  <span className={styles.itemText}>
                    <span>{t(item.label)}</span>
                    {item.hint && <small>{t(item.hint)}</small>}
                  </span>
                </button>
              ))}
            </div>
          ))}
          {loggedUser && (
            <button
              type="button"
              className={classNames(styles.drawerItem, styles.danger)}
              onClick={async () => {
                close();
                await logout();
              }}
            >
              <ThemeMenuIcon icon="logout" alt="" variant="footer" />
              <span className={styles.itemText}>
                <span>{t('logout_label')}</span>
              </span>
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}
